/**
 * Definiciones de proceso como datos (ADR 0005). Todos los valores son supuestos ilustrativos.
 *
 * - P0 y P1: procesos mínimos de prueba, calculables a mano (doc. 05 §2 y caso M5).
 * - Posventa 1.0: versión simplificada del proceso de referencia (doc. 04 §4.5) para la rebanada
 *   vertical 1.0. Rigen P1 (control de calidad administrativo, hecho por el mismo técnico) y P2
 *   (promesa en horas hábiles). Los ajustes del §4.6 llegan en el paso 1.2.
 */

import type { Distribucion } from '../nucleo/aleatoriedad/distribuciones';
import { REANUDAR, type DefinicionDeProceso, type Etapa } from '../nucleo/dominio/proceso';
import { supuesto } from '../nucleo/dominio/parametros';

const constante = (valor: number): Distribucion => ({ tipo: 'constante', valor });
const triangular = (min: number, tipico: number, max: number): Distribucion => ({
  tipo: 'triangular',
  min,
  tipico,
  max,
});

const SALIDA: Etapa = {
  id: 'salida',
  nombre: 'Salida del taller',
  tipo: 'salida',
  categoria: 'traslado',
  obligatoria: true,
  lugar: 'salida',
  personas: [],
  hitoAlIniciar: 'fin',
};

/** P0: recepción → servicio → lavado → salida, duraciones constantes (doc. 05 §2). */
export function procesoP0(): DefinicionDeProceso {
  return {
    id: 'prueba-p0',
    nombre: 'Proceso de prueba P0',
    version: 1,
    tiposDeUnidad: [
      {
        id: 'mantenimiento',
        nombre: 'Mantenimiento',
        promesaMin: supuesto(120, 'min'),
        horasVendidas: supuesto(1, 'h'),
        bahias: ['mixto', 'fast-lane'],
      },
    ],
    atributos: [],
    etapaInicial: 'recepcion',
    etapas: [
      {
        id: 'recepcion',
        nombre: 'Recepción con asesor',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        lugar: 'recepcion',
        personas: [{ rol: 'asesor' }],
        duracion: { porTipo: { '*': constante(15) }, origen: 'supuesto' },
        hitoAlTerminar: 'apertura-ot',
      },
      {
        id: 'servicio',
        nombre: 'Servicio',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        aplicaA: ['mantenimiento'],
        lugar: 'bahia',
        personas: [{ rol: 'tecnico' }],
        duracion: { porTipo: { '*': constante(60) }, origen: 'supuesto' },
        hitoAlTerminar: 'trabajo-terminado',
      },
      {
        id: 'lavado',
        nombre: 'Lavado',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        lugar: 'lavado',
        personas: [],
        duracion: { porTipo: { '*': constante(20) }, origen: 'supuesto' },
        hitoAlTerminar: 'listo',
      },
      SALIDA,
    ],
    transiciones: [
      { de: 'recepcion', a: 'servicio', sino: true },
      { de: 'servicio', a: 'lavado', sino: true },
      { de: 'lavado', a: 'salida', sino: true },
    ],
  };
}

/**
 * P0 con espera de repuesto a mitad del servicio, reteniendo la bahía (casos M6 y M11). El técnico
 * se retiene toda la espera, o se libera a los `liberarTrasMin` minutos (ajuste 2 del ADR 0013).
 */
export function procesoP0ConRepuesto(
  probabilidad = 1,
  esperaMin = 25,
  fraccion = 0.5,
  liberarTrasMin?: number,
): DefinicionDeProceso {
  const p = procesoP0();
  p.id = 'prueba-p0-repuesto';
  p.nombre = 'Proceso de prueba P0 con repuesto';
  p.atributos = [
    {
      id: 'requiereRepuesto',
      nombre: 'Requiere repuesto',
      probabilidad: supuesto({ mantenimiento: probabilidad }, ''),
    },
  ];
  const servicio = p.etapas.find((e) => e.id === 'servicio')!;
  servicio.interrupcion = {
    si: { atributo: 'requiereRepuesto', igual: true },
    fraccion: constante(fraccion),
    etapa: 'espera-repuesto',
    ...(liberarTrasMin !== undefined
      ? { liberarPersonasTras: supuesto(liberarTrasMin, 'min') }
      : {}),
  };
  p.etapas.splice(2, 0, {
    id: 'espera-repuesto',
    nombre: 'Espera de repuesto',
    tipo: 'espera-externa',
    categoria: 'espera',
    obligatoria: false,
    lugar: 'actual',
    personas: [],
    duracion: { porTipo: { '*': constante(esperaMin) }, origen: 'supuesto' },
  });
  return p;
}

/**
 * P0A = P0 con el adicional del mantenimiento (doc. 05, M20–M23; ADR 0017 y 0021): a la mitad del
 * servicio se detecta; llamada del asesor 5 min; aprobación del cliente 20 min hábiles; si aprueba,
 * trabajo extra 60 min con el técnico (+1.0 h vendida) y la promesa se corre 60 min hábiles; luego
 * vuelve al resto del servicio. El técnico se libera a los 15 min de la detección, o se retiene
 * toda la espera si `liberarTrasMin` es `null`. Cada OT trae forzado si tiene adicional y si aprueba.
 */
export function procesoP0A(liberarTrasMin: number | null = 15): DefinicionDeProceso {
  const p = procesoP0();
  p.id = 'prueba-p0a';
  p.nombre = 'Proceso de prueba P0A (adicional)';
  p.atributos = [
    {
      id: 'adicional',
      nombre: 'Tiene adicional',
      probabilidad: supuesto({ mantenimiento: 1 }, ''),
    },
    {
      id: 'apruebaAdicional',
      nombre: 'Aprueba el adicional',
      probabilidad: supuesto({ mantenimiento: 1 }, ''),
    },
  ];
  const servicio = p.etapas.find((e) => e.id === 'servicio')!;
  servicio.interrupcion = {
    si: { atributo: 'adicional', igual: true },
    fraccion: constante(0.5),
    etapa: 'adicional-llamada',
    ...(liberarTrasMin !== null ? { liberarPersonasTras: supuesto(liberarTrasMin, 'min') } : {}),
  };
  p.etapas.splice(2, 0, ...etapasDelAdicional(constante(5), constante(20), constante(60)));
  p.transiciones.push(...rutasDelAdicional());
  return p;
}

/** Etapas intercaladas del adicional: llamada, aprobación del cliente y trabajo extra. */
export function etapasDelAdicional(
  llamada: Distribucion,
  aprobacion: Distribucion,
  trabajo: Distribucion,
): Etapa[] {
  return [
    {
      id: 'adicional-llamada',
      nombre: 'Llamada al cliente por el adicional',
      tipo: 'actividad',
      categoria: 'administrativa',
      obligatoria: false,
      lugar: 'actual',
      personas: [{ rol: 'asesor' }],
      duracion: { porTipo: { '*': llamada }, origen: 'supuesto' },
    },
    {
      id: 'adicional-aprobacion',
      nombre: 'Aprobación del adicional',
      tipo: 'espera-externa',
      categoria: 'espera-cliente',
      obligatoria: false,
      lugar: 'actual',
      personas: [],
      duracion: { porTipo: { '*': aprobacion }, origen: 'supuesto' },
      ventanaHabil: { inicio: '08:00', fin: '18:00' },
    },
    {
      id: 'adicional-trabajo',
      nombre: 'Trabajo adicional',
      tipo: 'actividad',
      categoria: 'valor-agregado',
      obligatoria: false,
      lugar: 'actual',
      personas: [{ rol: 'tecnico', deLaUnidad: true }],
      duracion: { porTipo: { '*': trabajo }, origen: 'supuesto' },
    },
  ];
}

/**
 * Rutas del adicional: si aprueba, trabajo extra (la promesa se corre 60 min hábiles y suma 1.0 h
 * vendida); si no, vuelve al servicio. Después del trabajo extra, también vuelve.
 */
export function rutasDelAdicional(): DefinicionDeProceso['transiciones'] {
  return [
    { de: 'adicional-llamada', a: 'adicional-aprobacion', sino: true },
    {
      de: 'adicional-aprobacion',
      a: 'adicional-trabajo',
      si: { atributo: 'apruebaAdicional', igual: true },
      efectos: {
        correrPromesaMin: supuesto(60, 'min'),
        sumarHorasVendidas: supuesto(1, 'h'),
      },
    },
    { de: 'adicional-aprobacion', a: REANUDAR, sino: true },
    { de: 'adicional-trabajo', a: REANUDAR, sino: true },
  ];
}

/** P1 = P0 + diagnóstico, aprobación y reparación para «otros» (caso M5). */
export function procesoP1(): DefinicionDeProceso {
  const p = procesoP0();
  p.id = 'prueba-p1';
  p.nombre = 'Proceso de prueba P1';
  p.tiposDeUnidad.push({
    id: 'otros',
    nombre: 'Otros servicios',
    promesaMin: supuesto(240, 'min'),
    horasVendidas: supuesto(2, 'h'),
    bahias: ['mixto', 'mayor'],
  });
  const [recepcion, servicio, lavado, salida] = p.etapas as [Etapa, Etapa, Etapa, Etapa];
  p.etapas = [
    recepcion,
    {
      id: 'diagnostico',
      nombre: 'Diagnóstico',
      tipo: 'actividad',
      categoria: 'valor-agregado',
      obligatoria: false,
      lugar: 'bahia',
      personas: [{ rol: 'tecnico' }],
      duracion: { porTipo: { '*': constante(30) }, origen: 'supuesto' },
    },
    {
      id: 'aprobacion',
      nombre: 'Aprobación del cliente',
      tipo: 'espera-externa',
      categoria: 'espera-cliente',
      obligatoria: false,
      lugar: 'actual',
      personas: [],
      duracion: { porTipo: { '*': constante(45) }, origen: 'supuesto' },
    },
    servicio,
    {
      id: 'reparacion',
      nombre: 'Reparación',
      tipo: 'actividad',
      categoria: 'valor-agregado',
      obligatoria: false,
      lugar: 'bahia',
      personas: [{ rol: 'tecnico' }],
      duracion: { porTipo: { '*': constante(90) }, origen: 'supuesto' },
      hitoAlTerminar: 'trabajo-terminado',
    },
    lavado,
    salida,
  ];
  p.transiciones = [
    { de: 'recepcion', a: 'diagnostico', si: { tipoUnidad: 'otros' } },
    { de: 'recepcion', a: 'servicio', sino: true },
    { de: 'diagnostico', a: 'aprobacion', sino: true },
    { de: 'aprobacion', a: 'reparacion', sino: true },
    { de: 'servicio', a: 'lavado', sino: true },
    { de: 'reparacion', a: 'lavado', sino: true },
    { de: 'lavado', a: 'salida', sino: true },
  ];
  return p;
}

/**
 * P0 con la torre de control (caso M28; doc. 04 §4.5, n.º 2): entre la recepción y el servicio, la
 * asignación de la OT (7 min, administrativa, con el controlador, donde está el auto y espera en el
 * patio) reserva el elevador y el técnico del servicio.
 */
export function procesoP0ConTorre(): DefinicionDeProceso {
  const p = procesoP0();
  p.id = 'prueba-p0-torre';
  p.nombre = 'Proceso de prueba P0 con torre de control';
  p.etapas.splice(1, 0, {
    id: 'asignacion',
    nombre: 'Asignación de la OT (torre)',
    tipo: 'actividad',
    categoria: 'administrativa',
    obligatoria: true,
    lugar: 'actual',
    lugarDeEspera: 'patio',
    personas: [{ rol: 'controlador' }],
    duracion: { porTipo: { '*': constante(7) }, origen: 'supuesto' },
    reservaSiguiente: true,
  });
  p.transiciones = [
    { de: 'recepcion', a: 'asignacion', sino: true },
    { de: 'asignacion', a: 'servicio', sino: true },
    { de: 'servicio', a: 'lavado', sino: true },
    { de: 'lavado', a: 'salida', sino: true },
  ];
  return p;
}

/**
 * P0 con el técnico que va al almacén al empezar el servicio, ida y vuelta, con atención de 4 min
 * en el mostrador, solo en elevadores mixtos (caso M29).
 */
export function procesoP0ConAlmacen(): DefinicionDeProceso {
  const p = procesoP0();
  p.id = 'prueba-p0-almacen';
  p.nombre = 'Proceso de prueba P0 con recorrido al almacén';
  p.tiposDeUnidad[0]!.bahias = ['mixto', 'fast-lane'];
  p.etapas.find((e) => e.id === 'servicio')!.recorridos = [
    {
      quien: 'tecnico',
      desde: 'auto',
      hacia: 'almacen',
      idaYVuelta: true,
      atencion: constante(4),
      soloEnBahias: ['mixto', 'mayor'],
    },
  ];
  return p;
}

/**
 * P1 con la aprobación que puede rechazarse: si el cliente no aprueba, la OT va al lavado y se vende
 * como solo diagnóstico, 0.75 h (caso M26; regla de Renato, ADR 0021). Cada OT trae forzado si
 * aprueba.
 */
export function procesoP1SoloDiagnostico(): DefinicionDeProceso {
  const p = procesoP1();
  p.id = 'prueba-p1-solo-diagnostico';
  p.nombre = 'Proceso de prueba P1 con solo diagnóstico';
  p.atributos = [
    {
      id: 'apruebaReparacion',
      nombre: 'Aprueba la reparación',
      probabilidad: supuesto({ otros: 1 }, 'fracción'),
    },
  ];
  p.transiciones = p.transiciones.filter((t) => t.de !== 'aprobacion');
  p.transiciones.push(
    { de: 'aprobacion', a: 'reparacion', si: { atributo: 'apruebaReparacion', igual: true } },
    {
      de: 'aprobacion',
      a: 'lavado',
      sino: true,
      efectos: { terminarTrabajoConHoras: supuesto(0.75, 'h') },
    },
  );
  return p;
}

/**
 * P0 con control de calidad del responsable dedicado y bahía de espera de control (casos M14, M30 y
 * M36; reglas de Renato del 2026-11-04): al terminar el servicio, el auto pasa a la bahía (si está
 * llena, espera en el elevador) y ahí espera el control (10 min, administrativo). Opciones: corte
 * del control (`aceptaHasta`), el responsable camina hasta el auto y retrabajo forzado por atributo
 * (30 min, en un elevador, con prioridad y prefiriendo al técnico original, máximo 1 vez).
 */
export function procesoP0ConControl(
  o: { aceptaHasta?: string; camina?: boolean; retrabajo?: boolean } = {},
): DefinicionDeProceso {
  const p = procesoP0();
  p.id = 'prueba-p0-control';
  p.nombre = 'Proceso de prueba P0 con responsable de calidad y bahía de control';
  if (o.retrabajo) {
    p.atributos = [
      {
        id: 'retrabajo',
        nombre: 'No pasa el control',
        probabilidad: supuesto({ mantenimiento: 0 }, 'fracción'),
      },
    ];
  }
  p.etapas.splice(
    2,
    0,
    {
      id: 'espera-control',
      nombre: 'Espera de control (bahía)',
      tipo: 'actividad',
      categoria: 'espera',
      obligatoria: true,
      lugar: 'control',
      lugarDeEspera: 'actual',
      personas: [],
      duracion: { porTipo: { '*': constante(0) }, origen: 'supuesto' },
    },
    {
      id: 'control-calidad',
      nombre: 'Control de calidad',
      tipo: 'actividad',
      categoria: 'administrativa',
      obligatoria: true,
      lugar: 'actual',
      personas: [{ rol: 'calidad' }],
      duracion: { porTipo: { '*': constante(10) }, origen: 'supuesto' },
      ...(o.camina
        ? { recorridos: [{ quien: 'calidad', desde: 'donde-esta', hacia: 'auto' }] }
        : {}),
      ...(o.aceptaHasta ? { aceptaHasta: o.aceptaHasta } : {}),
    },
    ...(o.retrabajo
      ? [
          {
            id: 'retrabajo',
            nombre: 'Retrabajo',
            tipo: 'actividad',
            categoria: 'valor-agregado',
            obligatoria: false,
            lugar: 'bahia',
            lugarDeEspera: 'patio',
            prioridadAlta: true,
            personas: [{ rol: 'tecnico', prefiereAnterior: true }],
            duracion: { porTipo: { '*': constante(30) }, origen: 'supuesto' },
          } satisfies Etapa,
        ]
      : []),
  );
  p.transiciones = [
    { de: 'recepcion', a: 'servicio', sino: true },
    { de: 'servicio', a: 'espera-control', sino: true },
    { de: 'espera-control', a: 'control-calidad', sino: true },
    ...(o.retrabajo
      ? [
          {
            de: 'control-calidad',
            a: 'retrabajo',
            si: { atributo: 'retrabajo', igual: true },
            maximo: 1,
          },
          { de: 'retrabajo', a: 'espera-control', sino: true },
        ]
      : []),
    { de: 'control-calidad', a: 'lavado', sino: true },
    { de: 'lavado', a: 'salida', sino: true },
  ];
  return p;
}

/**
 * P1 con el repuesto del correctivo sin stock (caso M35; regla de Renato del 2026-11-04): si el
 * cliente aprueba y no hay repuesto, el auto sale del elevador al patio y espera la mercadería a la
 * mañana siguiente (`llegaEntre`, por defecto 08:00–10:00); la promesa pasa al día siguiente a las
 * 14:00 y luego pide elevador y técnico con prioridad para la reparación. Cada OT trae forzado si
 * aprueba y si hay stock.
 */
export function procesoP1SinStock(
  llegaEntre: [string, string] = ['08:00', '10:00'],
): DefinicionDeProceso {
  const p = procesoP1();
  p.id = 'prueba-p1-sin-stock';
  p.nombre = 'Proceso de prueba P1 con repuesto sin stock';
  p.atributos = [
    {
      id: 'apruebaReparacion',
      nombre: 'Aprueba la reparación',
      probabilidad: supuesto({ otros: 1 }, 'fracción'),
    },
    {
      id: 'sinStock',
      nombre: 'Repuesto sin stock',
      probabilidad: supuesto({ otros: 1 }, 'fracción'),
    },
  ];
  const i = p.etapas.findIndex((e) => e.id === 'reparacion');
  p.etapas[i] = { ...p.etapas[i]!, prioridadAlta: true };
  p.etapas.splice(i, 0, {
    id: 'espera-repuesto-sin-stock',
    nombre: 'Espera del repuesto sin stock',
    tipo: 'espera-externa',
    categoria: 'espera',
    obligatoria: false,
    lugar: 'patio',
    siNoHayLugarEsperaDondeEsta: true,
    personas: [],
    duracion: { porTipo: { '*': constante(0) }, origen: 'supuesto' },
    alDiaSiguiente: {
      probabilidad: supuesto(1, 'fracción'),
      desde: llegaEntre[0],
      hasta: llegaEntre[1],
    },
  });
  p.transiciones = p.transiciones.filter((t) => t.de !== 'aprobacion');
  p.transiciones.push(
    {
      de: 'aprobacion',
      a: 'espera-repuesto-sin-stock',
      si: {
        y: [
          { atributo: 'apruebaReparacion', igual: true },
          { atributo: 'sinStock', igual: true },
        ],
      },
      efectos: { promesaDiaSiguiente: supuesto('14:00', 'hh:mm') },
    },
    { de: 'aprobacion', a: 'reparacion', si: { atributo: 'apruebaReparacion', igual: true } },
    { de: 'aprobacion', a: 'lavado', sino: true },
    { de: 'espera-repuesto-sin-stock', a: 'reparacion', sino: true },
  );
  return p;
}

/**
 * Proceso de posventa de la rebanada 1.0 (supuestos ilustrativos, editables).
 * Rutas: mantenimiento = recepción → servicio → control de calidad → lavado → espera de retiro →
 * salida; otros = recepción → diagnóstico → aprobación → (aprueba: reparación → control de calidad)
 * → lavado → espera de retiro → salida.
 */
export function procesoPosventa(): DefinicionDeProceso {
  const repuesto = {
    si: { atributo: 'requiereRepuesto', igual: true },
    fraccion: { tipo: 'uniforme', min: 0.25, max: 0.65 } as Distribucion,
    etapa: 'espera-repuesto',
  };
  return {
    id: 'posventa-referencia',
    nombre: 'Posventa automotriz (referencia 1.0)',
    version: 1,
    tiposDeUnidad: [
      {
        id: 'mantenimiento',
        nombre: 'Mantenimiento',
        promesaMin: supuesto(180, 'min'),
        horasVendidas: supuesto(1.5, 'h'),
        bahias: ['mixto', 'fast-lane'],
      },
      {
        id: 'otros',
        nombre: 'Otros servicios',
        promesaMin: supuesto(360, 'min'),
        horasVendidas: supuesto(3, 'h'),
        bahias: ['mixto', 'mayor'],
      },
    ],
    atributos: [
      {
        id: 'requiereRepuesto',
        nombre: 'Requiere repuesto',
        probabilidad: supuesto({ mantenimiento: 0.1, otros: 0.25 }, ''),
      },
      {
        id: 'apruebaReparacion',
        nombre: 'Aprueba la reparación',
        probabilidad: supuesto({ otros: 0.9 }, ''),
      },
    ],
    etapaInicial: 'recepcion',
    etapas: [
      {
        id: 'recepcion',
        nombre: 'Recepción con asesor',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        lugar: 'recepcion',
        personas: [{ rol: 'asesor' }],
        duracion: { porTipo: { '*': triangular(10, 15, 25) }, origen: 'supuesto' },
        hitoAlTerminar: 'apertura-ot',
      },
      {
        id: 'diagnostico',
        nombre: 'Diagnóstico',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: false,
        lugar: 'bahia',
        lugarDeEspera: 'patio',
        personas: [{ rol: 'tecnico' }],
        duracion: { porTipo: { '*': triangular(25, 40, 70) }, origen: 'supuesto' },
      },
      {
        id: 'aprobacion',
        nombre: 'Aprobación del cliente',
        tipo: 'espera-externa',
        categoria: 'espera-cliente',
        obligatoria: false,
        lugar: 'actual',
        personas: [],
        duracion: { porTipo: { '*': triangular(15, 45, 120) }, origen: 'supuesto' },
        ventanaHabil: { inicio: '08:00', fin: '18:00' },
      },
      {
        id: 'servicio',
        nombre: 'Servicio de mantenimiento',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        aplicaA: ['mantenimiento'],
        lugar: 'bahia',
        lugarDeEspera: 'patio',
        personas: [{ rol: 'tecnico', retenerHasta: 'control-calidad' }],
        duracion: { porTipo: { '*': triangular(60, 90, 140) }, origen: 'supuesto' },
        interrupcion: repuesto,
        hitoAlTerminar: 'trabajo-terminado',
      },
      {
        id: 'reparacion',
        nombre: 'Reparación',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: false,
        lugar: 'bahia',
        personas: [{ rol: 'tecnico', retenerHasta: 'control-calidad' }],
        duracion: { porTipo: { '*': triangular(90, 150, 300) }, origen: 'supuesto' },
        interrupcion: repuesto,
        hitoAlTerminar: 'trabajo-terminado',
      },
      {
        id: 'espera-repuesto',
        nombre: 'Espera de repuesto',
        tipo: 'espera-externa',
        categoria: 'espera',
        obligatoria: false,
        lugar: 'actual',
        personas: [],
        duracion: { porTipo: { '*': triangular(10, 25, 60) }, origen: 'supuesto' },
      },
      {
        id: 'control-calidad',
        nombre: 'Control de calidad',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        aplicaA: ['mantenimiento'],
        lugar: 'actual',
        personas: [{ rol: 'tecnico', deLaUnidad: true }],
        duracion: { porTipo: { '*': triangular(5, 10, 20) }, origen: 'supuesto' },
      },
      {
        id: 'lavado',
        nombre: 'Lavado',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        lugar: 'lavado',
        lugarDeEspera: 'patio',
        personas: [],
        duracion: { porTipo: { '*': triangular(15, 20, 30) }, origen: 'supuesto' },
        hitoAlTerminar: 'listo',
      },
      {
        id: 'espera-retiro',
        nombre: 'Espera de retiro del cliente',
        tipo: 'espera-externa',
        categoria: 'espera-cliente',
        obligatoria: true,
        lugar: 'entrega',
        lugarDeEspera: 'patio',
        personas: [],
        duracion: { porTipo: { '*': triangular(20, 60, 180) }, origen: 'supuesto' },
        ventanaHabil: { inicio: '08:00', fin: '19:00' },
      },
      SALIDA,
    ],
    transiciones: [
      { de: 'recepcion', a: 'diagnostico', si: { tipoUnidad: 'otros' } },
      { de: 'recepcion', a: 'servicio', sino: true },
      { de: 'diagnostico', a: 'aprobacion', sino: true },
      { de: 'aprobacion', a: 'reparacion', si: { atributo: 'apruebaReparacion', igual: true } },
      { de: 'aprobacion', a: 'lavado', sino: true },
      { de: 'servicio', a: 'control-calidad', sino: true },
      { de: 'reparacion', a: 'control-calidad', sino: true },
      { de: 'control-calidad', a: 'lavado', sino: true },
      { de: 'lavado', a: 'espera-retiro', sino: true },
      { de: 'espera-retiro', a: 'salida', sino: true },
    ],
  };
}

/**
 * Proceso del Taller Demo (paso 1.6; propuesta aprobada el 2026-11-01 y reglas del 2026-11-04;
 * ADR 0013, 0017 y 0021). Rutas: mantenimiento = recepción → asignación (torre) → servicio (con
 * repuesto corto y adicional si les toca) → espera de control (bahía) → control de calidad
 * (responsable dedicado, corte 17:30; 3 % de retrabajo en un elevador, máximo 1) → lavado → espera
 * de retiro → entrega; otros = recepción → asignación → diagnóstico → aprobación → (aprueba con
 * repuesto en stock: reparación | aprueba sin stock: espera del repuesto en el patio hasta la mañana
 * siguiente, nueva promesa, reparación | no aprueba: solo diagnóstico, 0.75 h) → control → lavado →
 * espera de retiro → entrega. Todos los valores son supuestos ilustrativos, editables.
 */
export function procesoTallerDemo(): DefinicionDeProceso {
  const repuesto = (): Etapa['interrupcion'] => ({
    si: { atributo: 'requiereRepuesto', igual: true },
    fraccion: { tipo: 'uniforme', min: 0.25, max: 0.65 },
    etapa: 'espera-repuesto',
    liberarPersonasTras: supuesto(15, 'min'),
  });
  const almacen = {
    quien: 'tecnico' as const,
    desde: 'auto' as const,
    hacia: 'almacen' as const,
    idaYVuelta: true,
    atencion: triangular(2, 4, 8),
    soloEnBahias: ['mixto' as const, 'mayor' as const],
  };
  return {
    id: 'taller-demo',
    nombre: 'Posventa automotriz (Taller Demo)',
    version: 1,
    tiposDeUnidad: [
      {
        id: 'mantenimiento',
        nombre: 'Mantenimiento',
        promesaMin: supuesto(180, 'min'),
        horasVendidas: supuesto(1.5, 'h'),
        bahias: ['fast-lane', 'mixto'],
      },
      {
        id: 'otros',
        nombre: 'Otros servicios',
        promesaMin: supuesto(360, 'min'),
        horasVendidas: supuesto(3.5, 'h'),
        bahias: ['mayor', 'mixto'],
      },
    ],
    atributos: [
      {
        id: 'requiereRepuesto',
        nombre: 'Requiere repuesto (espera corta del mantenimiento)',
        probabilidad: supuesto({ mantenimiento: 0.1 }, 'fracción'),
      },
      {
        id: 'adicional',
        nombre: 'Tiene adicional',
        probabilidad: supuesto({ mantenimiento: 0.35 }, 'fracción'),
      },
      {
        id: 'apruebaAdicional',
        nombre: 'Aprueba el adicional',
        probabilidad: supuesto({ mantenimiento: 0.6 }, 'fracción'),
      },
      {
        id: 'apruebaReparacion',
        nombre: 'Aprueba la reparación',
        probabilidad: supuesto({ otros: 0.9 }, 'fracción'),
      },
      {
        id: 'sinStock',
        nombre: 'Repuesto sin stock (correctivo aprobado)',
        probabilidad: supuesto({ otros: 0.25 }, 'fracción'),
      },
      {
        id: 'retrabajo',
        nombre: 'No pasa el control (retrabajo)',
        probabilidad: supuesto({ mantenimiento: 0.03, otros: 0.03 }, 'fracción'),
      },
    ],
    etapaInicial: 'recepcion',
    etapas: [
      {
        id: 'recepcion',
        nombre: 'Recepción con asesor',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        lugar: 'recepcion',
        personas: [{ rol: 'asesor' }],
        duracion: { porTipo: { '*': triangular(10, 15, 25) }, origen: 'supuesto' },
        recorridos: [{ quien: 'cliente', desde: 'auto', hacia: 'oficina' }],
        hitoAlTerminar: 'apertura-ot',
      },
      {
        id: 'asignacion',
        nombre: 'Asignación de la OT (torre)',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        lugar: 'actual',
        lugarDeEspera: 'patio',
        personas: [{ rol: 'controlador' }],
        duracion: { porTipo: { '*': triangular(3, 7, 12) }, origen: 'supuesto' },
        reservaSiguiente: true,
      },
      {
        id: 'diagnostico',
        nombre: 'Diagnóstico',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: false,
        lugar: 'bahia',
        lugarDeEspera: 'patio',
        personas: [{ rol: 'tecnico', retenerHasta: 'aprobacion' }],
        duracion: { porTipo: { '*': triangular(30, 45, 75) }, origen: 'supuesto' },
      },
      {
        id: 'aprobacion',
        nombre: 'Aprobación del cliente',
        tipo: 'espera-externa',
        categoria: 'espera-cliente',
        obligatoria: false,
        lugar: 'actual',
        personas: [],
        duracion: {
          porTipo: { '*': { tipo: 'lognormal-mediana', mediana: 45, p95: 240, truncarEn: 480 } },
          origen: 'supuesto',
        },
        ventanaHabil: { inicio: '08:00', fin: '18:00' },
        liberarPersonasTras: supuesto(15, 'min'),
        alDiaSiguiente: { probabilidad: supuesto(0.1, 'fracción'), desde: '08:00', hasta: '11:00' },
      },
      {
        id: 'servicio',
        nombre: 'Servicio de mantenimiento',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        aplicaA: ['mantenimiento'],
        lugar: 'bahia',
        lugarDeEspera: 'patio',
        personas: [{ rol: 'tecnico' }],
        duracion: {
          porTipo: { '*': triangular(70, 90, 120) },
          porBahia: { 'fast-lane': { '*': triangular(35, 50, 75) } },
          origen: 'supuesto',
        },
        recorridos: [almacen],
        interrupcion: repuesto(),
        otrasInterrupciones: [
          {
            si: { atributo: 'adicional', igual: true },
            fraccion: { tipo: 'uniforme', min: 0.2, max: 0.6 },
            etapa: 'adicional-llamada',
            liberarPersonasTras: supuesto(15, 'min'),
          },
        ],
        hitoAlTerminar: 'trabajo-terminado',
      },
      ...etapasDelAdicional(
        triangular(3, 5, 10),
        { tipo: 'lognormal-mediana', mediana: 20, p95: 90, truncarEn: 240 },
        triangular(30, 60, 90),
      ).map((e) => (e.id === 'adicional-llamada' ? { ...e, aceptaHasta: '19:00' } : e)),
      {
        id: 'reparacion',
        nombre: 'Reparación',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: false,
        lugar: 'bahia',
        // Vuelve de la aprobación o del repuesto sin stock: pide elevador y técnico con prioridad.
        prioridadAlta: true,
        personas: [{ rol: 'tecnico' }],
        duracion: { porTipo: { '*': triangular(90, 150, 240) }, origen: 'supuesto' },
        recorridos: [almacen],
        hitoAlTerminar: 'trabajo-terminado',
      },
      {
        id: 'espera-repuesto',
        nombre: 'Espera de repuesto',
        tipo: 'espera-externa',
        categoria: 'espera',
        obligatoria: false,
        lugar: 'actual',
        personas: [],
        duracion: { porTipo: { '*': triangular(10, 25, 60) }, origen: 'supuesto' },
      },
      {
        id: 'espera-repuesto-sin-stock',
        nombre: 'Espera del repuesto sin stock',
        tipo: 'espera-externa',
        categoria: 'espera',
        obligatoria: false,
        lugar: 'patio',
        // Con el patio lleno, el auto espera el repuesto en el elevador (decisión menor del avance 5).
        siNoHayLugarEsperaDondeEsta: true,
        personas: [],
        // La mercadería llega a la mañana siguiente, entre las 08:00 y las 10:00.
        duracion: { porTipo: { '*': constante(0) }, origen: 'supuesto' },
        alDiaSiguiente: { probabilidad: supuesto(1, 'fracción'), desde: '08:00', hasta: '10:00' },
      },
      {
        id: 'espera-control',
        nombre: 'Espera de control (bahía)',
        tipo: 'actividad',
        categoria: 'espera',
        obligatoria: true,
        aplicaA: ['mantenimiento'],
        // El auto sale del elevador a la bahía de espera de control; si está llena, espera en el
        // elevador (Renato, 2026-11-04).
        lugar: 'control',
        lugarDeEspera: 'actual',
        personas: [],
        duracion: { porTipo: { '*': constante(0) }, origen: 'supuesto' },
      },
      {
        id: 'control-calidad',
        nombre: 'Control de calidad',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        aplicaA: ['mantenimiento'],
        lugar: 'actual',
        personas: [{ rol: 'calidad' }],
        duracion: { porTipo: { '*': triangular(5, 10, 20) }, origen: 'supuesto' },
        recorridos: [{ quien: 'calidad', desde: 'donde-esta', hacia: 'auto' }],
        // Corte del control (editable): ninguno empieza después de las 17:30.
        aceptaHasta: '17:30',
      },
      {
        id: 'retrabajo',
        nombre: 'Retrabajo',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: false,
        // Vuelve a pedir elevador y técnico con prioridad, prefiriendo al técnico original.
        lugar: 'bahia',
        // Si no hay elevador libre, espera en el patio para no trabar la bahía de control.
        lugarDeEspera: 'patio',
        prioridadAlta: true,
        personas: [{ rol: 'tecnico', prefiereAnterior: true }],
        duracion: { porTipo: { '*': triangular(15, 30, 60) }, origen: 'supuesto' },
      },
      {
        id: 'lavado',
        nombre: 'Lavado',
        tipo: 'actividad',
        categoria: 'valor-agregado',
        obligatoria: true,
        lugar: 'lavado',
        lugarDeEspera: 'patio',
        personas: [],
        duracion: { porTipo: { '*': triangular(15, 20, 30) }, origen: 'supuesto' },
        hitoAlTerminar: 'listo',
      },
      {
        id: 'espera-retiro',
        nombre: 'Espera de retiro del cliente',
        tipo: 'espera-externa',
        categoria: 'espera-cliente',
        obligatoria: true,
        lugar: 'entrega',
        lugarDeEspera: 'patio',
        personas: [],
        regreso: {
          cercaDePromesa: supuesto(0.65, 'fracción'),
          desviacion: triangular(-15, 10, 60),
          finDeTarde: { desde: '17:00', hasta: '19:00' },
          aviso: triangular(20, 40, 90),
          limite: '19:00',
          siguienteDia: { desde: '08:00', hasta: '12:00' },
        },
      },
      {
        id: 'entrega',
        nombre: 'Entrega con asesor',
        tipo: 'actividad',
        categoria: 'administrativa',
        obligatoria: true,
        lugar: 'actual',
        personas: [{ rol: 'asesor' }],
        duracion: { porTipo: { '*': triangular(5, 10, 20) }, origen: 'supuesto' },
        recorridos: [
          { quien: 'cliente', desde: 'acceso-peatonal', hacia: 'oficina' },
          { quien: 'cliente', desde: 'oficina', hacia: 'auto' },
        ],
        // Los asesores entregan hasta el límite de retiro, en horas extra (Renato, 2026-11-04).
        aceptaHasta: '19:00',
      },
      SALIDA,
    ],
    transiciones: [
      { de: 'recepcion', a: 'asignacion', sino: true },
      { de: 'asignacion', a: 'diagnostico', si: { tipoUnidad: 'otros' } },
      { de: 'asignacion', a: 'servicio', sino: true },
      { de: 'diagnostico', a: 'aprobacion', sino: true },
      {
        de: 'aprobacion',
        a: 'espera-repuesto-sin-stock',
        si: {
          y: [
            { atributo: 'apruebaReparacion', igual: true },
            { atributo: 'sinStock', igual: true },
          ],
        },
        efectos: { promesaDiaSiguiente: supuesto('14:00', 'hh:mm') },
      },
      { de: 'aprobacion', a: 'reparacion', si: { atributo: 'apruebaReparacion', igual: true } },
      { de: 'espera-repuesto-sin-stock', a: 'reparacion', sino: true },
      {
        de: 'aprobacion',
        a: 'lavado',
        sino: true,
        efectos: { terminarTrabajoConHoras: supuesto(0.75, 'h') },
      },
      { de: 'servicio', a: 'espera-control', sino: true },
      ...rutasDelAdicional(),
      { de: 'reparacion', a: 'espera-control', sino: true },
      { de: 'espera-control', a: 'control-calidad', sino: true },
      {
        de: 'control-calidad',
        a: 'retrabajo',
        si: { atributo: 'retrabajo', igual: true },
        maximo: 1,
      },
      { de: 'control-calidad', a: 'lavado', sino: true },
      { de: 'retrabajo', a: 'espera-control', sino: true },
      { de: 'lavado', a: 'espera-retiro', sino: true },
      { de: 'espera-retiro', a: 'entrega', sino: true },
      { de: 'entrega', a: 'salida', sino: true },
    ],
  };
}
